<template>
  <div
    v-if="initialized && project_id"
    class="flex h-full w-full"
  >
    <Sidebar v-if="!mq.isMobile" />
    <div class="flex h-full min-w-0 flex-1 flex-col">
      <TheNavbarTop v-if="mq.isMobile" />
      <OutOfOfficeBanner
        v-if="current_client.attributes.ooo"
        :client="current_client"
        :profile="current_profile"
      />

      <main
        class="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 scrollbar-thumb-rounded-full scrollbar-gutter-stable h-full w-full overflow-y-auto scroll-smooth"
      >
        <div
          class="mx-auto flex max-w-[1800px] flex-col px-5 py-3"
          :class="{ 'h-full': $route.params.ticket_id }"
        >
          <router-view />
        </div>
      </main>
    </div>
    <TheNotificationsSidebar v-show="show_notification_sidebar" />
    <SurveysPopup
      v-if="surveys_popup_active"
      :project_id="project_id"
      :survey_id="active_survey_id"
      @close="closeSurveysPopup"
    />
    <ButtonImpersonateStop
      v-if="current_profile_true.type === PROFILE_TYPES.EMPLOYEE"
      :profile="current_profile"
    />
  </div>
</template>

<script setup>
import Sidebar from '@/app/_customers/components/navbar/TheSidebar.vue';
import SurveysPopup from '@/app/shared_components/surveys/SurveysPopup.vue';
import ButtonImpersonateStop from '@/app/shared_components/buttons/ButtonImpersonateStop.vue';
import TheNavbarTop from '@/app/_customers/components/navbar/TheNavbarTop.vue';
import TheNotificationsSidebar from '@/app/shared_components/navbar/notifications/TheNotificationsSidebar.vue';
import OutOfOfficeBanner from '@/app/shared_components/banner/TheOutOfOfficeBanner.vue';
import SIDEBAR_NOTIFICATIONS from '@/app/data/sidebar_constants';
import { PROFILE_TYPES, Tenant } from '@/app/data/model_constants';
import { ROUTES_CUSTOMERS } from '@/app/data/route_constants';
import { computed, getCurrentInstance, inject, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useProfile from '@/app/composables/useProfile';
import INJECT_KEYS from '@/app/data/inject_keys';

const props = defineProps({
  project_id: { type: String, default: null },
});

const route = useRoute();
const router = useRouter();
const emitter = inject(INJECT_KEYS.EMITTER);
const { t } = useI18n();
const { proxy } = getCurrentInstance();

const { current_client, current_profile, current_profile_true, initialized, initProfile, projects, sidebar_type } =
  useProfile();

const active_survey_id = ref(null);
const surveys_popup_active = ref(true);

const show_notification_sidebar = computed(() => sidebar_type.value === SIDEBAR_NOTIFICATIONS);

const is_birthday = computed(() => {
  if (current_client.value.attributes.status === Tenant.STATUS_TEST) return false;

  const birth_date = new Date(current_profile.value.attributes.birth_date);
  const today = new Date();
  return birth_date && birth_date.getMonth() === today.getMonth() && birth_date.getDate() === today.getDate();
});

function initConfetti() {
  if (!is_birthday.value) return;
  if (surveys_popup_active.value) return; // no confetti with survey modal, invoke on closeSurveysPopup()

  const confetti_at = new Date().getFullYear().toString();
  const confettied_at = localStorage.getItem('confettied_at');
  if (confettied_at && confettied_at === confetti_at) return;
  proxy.$buefy.toast.open({
    message: t('customer.toasts.birthday', { first_name: current_profile.value.attributes.first_name }),
    type: 'is-success',
    duration: 5000,
  });
  proxy.$confetti.start();
  setTimeout(() => proxy.$confetti.stop(), 5000);
  localStorage.setItem('confettied_at', confetti_at);
}

function closeSurveysPopup() {
  surveys_popup_active.value = false;
  initConfetti();
}

function initProject() {
  if (_.isEmpty(projects.value)) {
    // customer does not have any (visible) units: trigger logout
    emitter.$emit('logout');
  } else {
    // project_id route param takes precedence, scoped with fallback to first unit's project
    let project_id = route.params.project_id;
    if (project_id && !(project_id in projects.value)) {
      project_id = null;
    }
    if (!project_id) {
      const project = _.chain(projects.value).values(projects.value).maxBy('attributes.created_at').value();
      project_id = project.id;
      let route_name = route.name;
      // convert "root" routes to project namespaced routes
      if (route.name === ROUTES_CUSTOMERS.ROOT) {
        route_name = ROUTES_CUSTOMERS.DASHBOARD;
      } else if (route.name === ROUTES_CUSTOMERS.ROOT_SETTINGS_PROFILE) {
        route_name = ROUTES_CUSTOMERS.SETTINGS_PROFILE;
      }
      router.replace({ name: route_name, params: { project_id } });
    }
  }
}

watch(
  () => props.project_id,
  () => {
    surveys_popup_active.value = true;
  },
);

watch(
  initialized,
  (value) => {
    if (value) initConfetti();
  },
  { immediate: true },
);

// survey popup handler
emitter.$on('initSurvey', (survey_id) => {
  active_survey_id.value = survey_id;
  surveys_popup_active.value = true;
});

onBeforeMount(async () => {
  await initProfile();
  initProject();
});
</script>
